import GameModel from "../../GameModel";
import Projection from "../../Projection";
import { Settings } from "../../Settings";
import NumberFormatter from "../../util/NumberFormatter";
import GridTile from "../grid/GridTile";
import BigNum from "../math/BigNum";
import Vector from "../math/Vector";
import InfoText from "./InfoText";
import InfoTextCache from "./InfoTextCache";

export default class InfoTextProcessor extends GameModel {
    /**
     * @param {Projection} projection 
     */
    constructor(projection) {
        super();

        /**
         * @constant
         * @type {Projection}
         */
        this.projection = projection;

        /**
         * @constant
         * @type {InfoTextCache}
         */
        this.infoTextCache = new InfoTextCache();

        /**
         * @constant
         * @type {Array.<InfoText>}
         */
        this.infoTextArray = [];

        /**
         * @private
         * @constant
         * @type {Vector}
         */
        this.workingVector = new Vector();
    
    }

    /**
     * @param {GridTile} tile 
     * @param {BigNum} ore 
     */
    addOreText(tile, ore) {

        // Don't bother if the tile is not visible
        if (!this.projection.isVisibleGridBox(tile)) {
            return;
        }

        const text = NumberFormatter.formatBigNum(ore);

        this.addCurrencyInternal(tile, text);
    }

    /**
     * @param {GridTile} tile 
     * @param {BigNum} gold 
     */
    addGoldText(tile, gold) {

        // Don't bother if the tile is not visible
        if (!this.projection.isVisibleGridBox(tile)) {
            return;
        }

        const text = NumberFormatter.formatBigNum(gold) + ' Gold!';

        this.addCurrencyInternal(tile, text);
    }


    /**
     * @param {GridTile} tile 
     * @param {BigNum} ruby 
     */
    addRubyText(tile, ruby) {

        // Don't bother if the tile is not visible
        if (!this.projection.isVisibleGridBox(tile)) {
            return;
        }

        const text = NumberFormatter.formatBigNum(ruby) + ' Ruby!';

        this.addCurrencyInternal(tile, text);
    }

    /**
     * @private
     * @param {GridTile} tile 
     * @param {string} text 
     */
    addCurrencyInternal(tile, text) {

        // Don't bother if the tile is not visible
        if (!this.projection.isVisibleGridBox(tile)) {
            return;
        }

        // If there are too many text objects, remove some
        while (this.infoTextArray.length >= Settings.text.maxInfoTextCount) {
            this.infoTextArray[0].reset(true);
            this.infoTextArray.splice(0, 1);
        }

        /**
         * @type {InfoText}
         */
        const infoText = this.infoTextCache.get();
        infoText.set(text);

        this.setTextPosition(infoText, tile.origin);
        this.infoTextArray.push(infoText);
    }


    /**
     * @private
     * @param {InfoText} infoText 
     * @param {Vector} position 
     */
    setTextPosition(infoText, position) {
        // Convert the position into screen coordinates.
        this.projection.worldToScreen(position, this.workingVector);

        infoText.textX = this.workingVector.x + Settings.tile.halfSize;
        infoText.textY = this.workingVector.y; // - Settings.tile.halfSize;
    }

    /**
     * @param {number} frameTimeRatio 
     */
    updateTextForFrame(frameTimeRatio) {

        for (let i = this.infoTextArray.length - 1; i >= 0; i--) {
            const infoText = this.infoTextArray[i];
            if (infoText.updateTextForFrame(frameTimeRatio)) {
                infoText.reset(true);
                this.infoTextArray.splice(i, 1);
            }
        }
    }

    clearInfoText() {
        if (this.infoTextArray.length > 0) {
            this.resetInfoTextList(this.infoTextArray);
            this.infoTextArray.length = 0;
        }
    }

    /**
     * @private
     * @param {Array.<InfoText>} infoTextList 
     */
    resetInfoTextList(infoTextList) {
        for (let i = 0; i < infoTextList.length; i++) {
            infoTextList[i].reset(true);
        }
    }

    ////////////////////////////////////
    // GameModel
    ////////////////////////////////////

    /**
     * @override
     */
    resetFull() {
        this.infoTextArray.length = 0;
        this.infoTextCache.resetCache();
    }

    /**
     * @override
     */
    resetForPrestige() {
        this.resetFull();
    }
    
}